import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Button, Field, HStack, Input, InputGroup, Text, VStack } from '@chakra-ui/react';
import { EmptyState } from '@src/components/chakra-ui/empty-state';
import { useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { SessionEarningsFeeNotice } from '@src/components/sco/Advisors/shared/SessionEarningsFeeNotice';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useState } from 'react';
import { LuSave, LuUserRound } from 'react-icons/lu';
const MAX_HOURLY_RATE = 10000;
type MyProfileData = {
  myAdvisorProfile?: ScoAdvisorProfile | null;
};
export type SessionSettingsViewProps = {
  hourlyRate: string;
  feePercent?: number | null;
  wilUpgradeHref: string;
  teamUpgradeHref: string;
  error: string | null;
  saved: boolean;
  saving: boolean;
  onHourlyRateChange: (value: string) => void;
  onSave: () => void;
};
const parsedHourlyRate = (hourlyRate: string): number => {
  if (hourlyRate.trim() === '') {
    return Number.NaN;
  }
  return Number(hourlyRate);
};
const rateInvalid = (hourlyRate: string): boolean => {
  if (hourlyRate.trim() === '') {
    return false;
  }
  const rate = parsedHourlyRate(hourlyRate);
  return !Number.isFinite(rate) || rate < 0 || rate > MAX_HOURLY_RATE;
};
export const SessionSettingsView = t0 => {
  const $ = _c(38);
  const {
    hourlyRate,
    feePercent,
    wilUpgradeHref,
    teamUpgradeHref,
    error,
    saved,
    saving,
    onHourlyRateChange,
    onSave
  } = t0;
  let t1;
  if ($[0] !== hourlyRate) {
    const parsedRate = parsedHourlyRate(hourlyRate);
    t1 = Number.isFinite(parsedRate) && parsedRate >= 0 ? parsedRate : null;
    $[0] = hourlyRate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const exampleRate = t1;
  let t2;
  if ($[2] !== hourlyRate) {
    t2 = rateInvalid(hourlyRate);
    $[2] = hourlyRate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const invalid = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Field.Label>Hourly rate (USD)</Field.Label>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onHourlyRateChange) {
    t4 = event => onHourlyRateChange(event.target.value);
    $[5] = onHourlyRateChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== hourlyRate || $[8] !== t4) {
    t5 = <InputGroup endAddon="/ hour" flex="1" maxW="sm" minW="12rem" startAddon="$"><Input max={MAX_HOURLY_RATE} min={0} placeholder="0" step="1" type="number" value={hourlyRate} onChange={t4} /></InputGroup>;
    $[7] = hourlyRate;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LuSave />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onSave || $[12] !== saving) {
    t7 = <Button flexShrink={0} loading={saving} variant="cta" onClick={onSave}>{t6} Save Rate</Button>;
    $[11] = onSave;
    $[12] = saving;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <HStack align="center" gap={3} wrap="wrap">{t5}{t7}</HStack>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Field.HelperText>Founders book 15, 30, 45, or 60 minutes at this listed rate. $0 is a free session.</Field.HelperText>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== invalid) {
    t10 = invalid ? <Field.ErrorText>Enter a rate between 0 and {MAX_HOURLY_RATE}.</Field.ErrorText> : null;
    $[18] = invalid;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== invalid || $[21] !== t10 || $[22] !== t8) {
    t11 = <Field.Root required={true} invalid={invalid}>{t3}{t8}{t9}{t10}</Field.Root>;
    $[20] = invalid;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== error) {
    t12 = error ? <Text color="red.fg" fontFamily="longform" fontSize="sm">{error}</Text> : null;
    $[24] = error;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== saved) {
    t13 = saved ? <Text color="green.fg" fontFamily="longform" fontSize="sm">Rates saved.</Text> : null;
    $[26] = saved;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== exampleRate || $[29] !== feePercent || $[30] !== teamUpgradeHref || $[31] !== wilUpgradeHref) {
    t14 = <SessionEarningsFeeNotice feePercent={feePercent} hourlyRate={exampleRate} teamUpgradeHref={teamUpgradeHref} wilUpgradeHref={wilUpgradeHref} />;
    $[28] = exampleRate;
    $[29] = feePercent;
    $[30] = teamUpgradeHref;
    $[31] = wilUpgradeHref;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <VStack align="stretch" gap={6}>{t11}{t12}{t13}{t14}</VStack>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};
const SessionSettingsForm = t0 => {
  const $ = _c(16);
  const {
    profile
  } = t0;
  const {
    currentUser
  } = useCurrentUser();
  const {
    wilUpgradeHref,
    teamUpgradeHref
  } = useAdvisorsAccess();
  const feePercent = currentUser?.contentPermissions?.sessionEarningsFeeAsPercent;
  const [hourlyRate, setHourlyRate] = useState(profile.hourlyRate != null ? String(profile.hourlyRate) : "");
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchQueries: [{
        query: MY_ADVISOR_PROFILE
      }]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [upsert, upsertState] = useMutation(UPSERT_ADVISOR_PROFILE, t1);
  let t2;
  if ($[1] !== hourlyRate || $[2] !== upsert) {
    t2 = async () => {
      setError(null);
      setSaved(false);
      const rate = hourlyRate.trim() === "" ? null : Number(hourlyRate);
      if (rate != null && (Number.isNaN(rate) || rate < 0 || rate > MAX_HOURLY_RATE)) {
        setError(`Hourly rate must be between 0 and ${MAX_HOURLY_RATE}.`);
        return;
      }
      ;
      try {
        await upsert({
          variables: {
            input: {
              hourlyRate: rate
            }
          }
        });
        setSaved(true);
      } catch (t3) {
        const err = t3;
        setError(cleanErrorMessage(err, "Could not save session settings."));
      }
    };
    $[1] = hourlyRate;
    $[2] = upsert;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleSave = t2;
  let t3;
  if ($[4] !== handleSave) {
    t3 = () => void handleSave();
    $[4] = handleSave;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      setHourlyRate(value);
      setSaved(false);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== error || $[8] !== feePercent || $[9] !== hourlyRate || $[10] !== saved || $[11] !== t3 || $[12] !== teamUpgradeHref || $[13] !== upsertState.loading || $[14] !== wilUpgradeHref) {
    t5 = <SessionSettingsView error={error} feePercent={feePercent} hourlyRate={hourlyRate} saved={saved} saving={upsertState.loading} teamUpgradeHref={teamUpgradeHref} wilUpgradeHref={wilUpgradeHref} onSave={t3} onHourlyRateChange={t4} />;
    $[7] = error;
    $[8] = feePercent;
    $[9] = hourlyRate;
    $[10] = saved;
    $[11] = t3;
    $[12] = teamUpgradeHref;
    $[13] = upsertState.loading;
    $[14] = wilUpgradeHref;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const SessionSettingsSection = () => {
  const $ = _c(8);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const profileQuery = useQuery(MY_ADVISOR_PROFILE, t0);
  const profile = profileQuery.data?.myAdvisorProfile;
  const t1 = profileQuery.loading && !profile;
  let t2;
  if ($[1] !== paths || $[2] !== profile) {
    t2 = profile ? <SessionSettingsForm profile={profile} /> : <EmptyState description="Finish the application, then return here to enable paid sessions." icon={<LuUserRound />} size="sm" title="Create an advisor profile first"><Button asChild={true} variant="cta"><NextLink href={paths.apply}>Apply to advise</NextLink></Button></EmptyState>;
    $[1] = paths;
    $[2] = profile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== profileQuery.error || $[5] !== t1 || $[6] !== t2) {
    t3 = <AdvisorsQueryState error={profileQuery.error} loading={t1}>{t2}</AdvisorsQueryState>;
    $[4] = profileQuery.error;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};